import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { works } from "@/mocks/works";
import { news } from "@/mocks/news";
import { characters } from "@/mocks/characters";
import WorkCard from "@/components/feature/WorkCard";
import NewsCard from "@/components/feature/NewsCard";
import CharacterCard from "@/components/feature/CharacterCard";
import Breadcrumbs from "@/components/feature/Breadcrumbs";

type TabKey = "works" | "news" | "characters";

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: "works", label: "影视作品", icon: "ri-film-line" },
  { key: "news", label: "资讯", icon: "ri-newspaper-line" },
  { key: "characters", label: "角色", icon: "ri-user-star-line" },
];

export default function Search() {
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState(q);
  const [tab, setTab] = useState<TabKey>("works");

  useSeo({
    title: q ? `搜索“${q}” - 希岛爱理影视内容站` : "搜索 - 希岛爱理影视内容站",
    description: q
      ? `在希岛爱理影视内容站中搜索“${q}”，查找相关的影视作品、资讯与角色解析内容。`
      : "在希岛爱理影视内容站中搜索影视作品、资讯与角色解析内容。",
    keywords: "搜索,希岛爱理,影视作品搜索,资讯搜索,角色搜索",
  });

  const query = q.trim().toLowerCase();

  const matchedWorks = useMemo(() => {
    if (!query) return [];
    return works.filter((w) =>
      [w.title, w.director, w.type, w.synopsis, w.cast.join(" ")]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [query]);

  const matchedNews = useMemo(() => {
    if (!query) return [];
    return news.filter((n) =>
      [n.title, n.summary, n.content.join(" ")].join(" ").toLowerCase().includes(query)
    );
  }, [query]);

  const matchedCharacters = useMemo(() => {
    if (!query) return [];
    return characters.filter((c) =>
      [c.name, c.workTitle, c.description, c.quote].join(" ").toLowerCase().includes(query)
    );
  }, [query]);

  const counts: Record<TabKey, number> = {
    works: matchedWorks.length,
    news: matchedNews.length,
    characters: matchedCharacters.length,
  };
  const totalCount = counts.works + counts.news + counts.characters;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  const reset = () => {
    setKeyword("");
    navigate("/search");
  };

  const currentResults =
    tab === "works" ? matchedWorks.length : tab === "news" ? matchedNews.length : matchedCharacters.length;

  return (
    <div className="w-full pb-16">
      <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <div className="w-full px-4 md:px-8 pt-6">
        <div className="rounded-lg bg-primary-950 text-background-100 p-6 md:p-10">
          <span className="text-xs font-label font-semibold tracking-[0.2em] uppercase text-accent-400">
            Search
          </span>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mt-2">
            站内搜索
          </h1>
          <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="flex-1 flex items-center gap-2 bg-background-50 rounded-md pl-3 pr-1 py-1">
              <i className="ri-search-line text-foreground-400" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入作品名、演员名、角色名或关键词"
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none py-2"
              />
            </div>
            <button
              type="submit"
              className="px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 pt-8">
        {q ? (
          <>
            <p className="text-sm text-foreground-600">
              关键词 <span className="font-semibold text-foreground-950">“{q}”</span> 共找到{" "}
              <span className="font-semibold text-accent-600">{totalCount}</span> 条结果
            </p>

            <div className="mt-5 flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit max-w-full overflow-x-auto no-scrollbar">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    tab === t.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:bg-background-50"
                  }`}
                >
                  <i className={t.icon} />
                  {t.label}
                  <span
                    className={`ml-0.5 text-xs px-1.5 rounded-full ${
                      tab === t.key ? "bg-background-50/25" : "bg-background-200 text-foreground-600"
                    }`}
                  >
                    {counts[t.key]}
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-8">
              {currentResults === 0 ? (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
                  <i className="ri-search-eye-line text-4xl text-foreground-300" />
                  <p className="mt-3 text-foreground-700">该类别下没有匹配的结果</p>
                  <p className="text-sm text-foreground-500 mt-1">试试切换类别或更换关键词</p>
                  <button
                    type="button"
                    onClick={reset}
                    className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line" />
                    重置搜索
                  </button>
                </div>
              ) : (
                <>
                  {tab === "works" && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                      {matchedWorks.map((w) => (
                        <WorkCard key={w.id} work={w} />
                      ))}
                    </div>
                  )}
                  {tab === "news" && (
                    <div className="flex flex-col gap-4">
                      {matchedNews.map((n) => (
                        <NewsCard key={n.id} item={n} layout="row" />
                      ))}
                    </div>
                  )}
                  {tab === "characters" && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                      {matchedCharacters.map((c) => (
                        <CharacterCard key={c.id} character={c} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
            <i className="ri-search-line text-4xl text-foreground-300" />
            <p className="mt-3 text-foreground-700">输入关键词开始搜索</p>
            <p className="text-sm text-foreground-500 mt-1">支持搜索影视作品、资讯与角色解析</p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              {["海边的信", "冬日回声", "真纪", "无声告白"].map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-3.5 py-1.5 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}